import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { App } from './App';
import { GA4RouteTracker } from './components/Analytics/GA4RouteTracker';
import { VisitorTracker } from './components/Analytics/VisitorTracker';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SmartFeedbackProvider } from './contexts/SmartFeedbackContext';
import { ToastProvider } from './contexts/ToastContext';
import { UpgradeModalProvider } from './contexts/UpgradeModalContext';
import { mainRoutes } from './routes/mainRoutes';
import { protectedRoutes } from './routes/protectedRoutes';
const GlobalModals = lazy(() => import('./components/GlobalModals').then(module => ({
  default: module.GlobalModals
})));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage').then(module => ({
  default: module.NotFoundPage
})));
const StaticHtmlPage = lazy(() => import('./pages/StaticHtmlPage').then(module => ({
  default: module.StaticHtmlPage
})));
const PUBLIC_PREFIXES = ['/about', '/blog', '/faq', '/privacy', '/terms', '/es', '/fr', '/de', '/pt', '/ja', '/ko', '/hi', '/id'];
function CatchAllRoute() {
  const location = useLocation();
  const isPublic = PUBLIC_PREFIXES.some(prefix => location.pathname === prefix || location.pathname.startsWith(`${prefix}/`));
  return isPublic ? <StaticHtmlPage data-id="element-63" data-mp="1q3loyc8ldlu1" /> : <NotFoundPage data-id="element-64" data-mp="1l2fv2b373six" />;
}
function PageLoader() {
  return <div className="flex min-h-screen items-center justify-center bg-[#050505]" data-id="element-65" data-mp="1l3zbc1ouq3kp">
      <div className="flex flex-col items-center gap-4" data-id="element-66" data-mp="200qbid6cnjbd">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-red-500 border-t-transparent" data-id="element-67" data-mp="1v0lu8kxmakk3" />
        <span className="font-mono text-sm tracking-wider text-gray-400" data-id="element-68" data-mp="3belrtp8zpy46">
          LOADING...
        </span>
      </div>
    </div>;
}
const ONBOARDING_EXEMPT_PREFIXES = ['/onboarding', '/auth', '/login', '/signup', '/payment', '/loading', '/results', '/about', '/privacy', '/terms', '/faq', '/blog', '/test-google', '/debug', '/es', '/fr', '/de', '/pt', '/ja', '/ko', '/hi', '/id'];
function OnboardingRedirectGuard() {
  const {
    user,
    loading
  } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (loading || !user) return;
    const completed = (user.user_metadata as {
      completedOnboarding?: boolean;
    }).completedOnboarding;
    if (completed) return;
    const isExempt = ONBOARDING_EXEMPT_PREFIXES.some(prefix => location.pathname === prefix || location.pathname.startsWith(`${prefix}/`));
    if (!isExempt) {
      navigate('/onboarding', {
        replace: true
      });
    }
  }, [loading, location.pathname, navigate, user]);
  return null;
}
export function AppRouter() {
  return <BrowserRouter data-id="element-69" data-mp="2gzrtb8pyc997">
      <AuthProvider data-id="element-70" data-mp="2gz89fmgqrasj">
        <ToastProvider data-id="element-71" data-mp="y5yfdeomzj8f">
          <SmartFeedbackProvider data-id="element-72" data-mp="y6gi8u5t0vpb">
            <UpgradeModalProvider data-id="element-73" data-mp="y5eym1aq30vz">
              <GA4RouteTracker data-id="element-74" data-mp="u4d8c9ys9z65" />
              <VisitorTracker data-id="element-75" data-mp="u43hkk5wznze" />
              <OnboardingRedirectGuard data-id="element-76" data-mp="u4v3t4dmg9d0" />
              <Suspense fallback={null} data-id="element-77" data-mp="p509sryjv3nq">
                <GlobalModals data-id="element-78" data-mp="p3yxkk5ssb4f" />
              </Suspense>
              <Suspense fallback={<PageLoader data-id="element-80" data-mp="k6yri98y78db" />} data-id="element-79" data-mp="k6fkvoor06el">
                <Routes data-id="element-81" data-mp="k5nnq9fi60m7">
                  <Route path="/" element={<App data-id="element-83" data-mp="f6ub0j81idn3" />} data-id="element-82" data-mp="k8jmfzz6ctbp" />
                  {mainRoutes}
                  {protectedRoutes}
                  <Route path="*" element={<CatchAllRoute data-id="element-85" data-mp="ag41quwhtqn4" />} data-id="element-84" data-mp="agmagr0cdxbx" />
                </Routes>
              </Suspense>
            </UpgradeModalProvider>
          </SmartFeedbackProvider>
        </ToastProvider>
      </AuthProvider>
    </BrowserRouter>;
}